import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import {
  animationContainer,
  animationWrapper,
  iconContainer,
  sendGiftButton,
  sendGiftContainer,
  sendGiftTextWrapper,
  sendGiftTitle,
  sendGiftWrapper
} from './styles.ts';

import animData from '@/components/common/svg/assets/sparks_gift.json';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { CandidateProfileApi } from '@/api/matchmaker/types.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import Modal from '@/animations/Modal.tsx';
import SendGiftPopup from '@/components/Discovery/SendGiftPopup/SendGiftPopup.tsx';
import {
  Gender,
  ProductInvoicePlacements,
  RelationKind
} from '@/enums/apiEnums.ts';
import { useSummariesStore } from '@/store/useSummariesStore.ts';
import { useDiscoveryStore } from '@/store/useDiscoveryStore.ts';
import LottieAnimation from '@/animations/LottieAnimation.tsx';
import FireReactionShape from '@/components/common/svg/shapes/FireReactionShape.tsx';

type SendGiftBlockProps = {
  candidate: CandidateProfileApi;
  relationKind: RelationKind;
};

export const SendGiftBlock = ({
  candidate,
  relationKind
}: SendGiftBlockProps) => {
  const { t } = useTranslation();

  const [giftSent, setGiftSent] = useState<boolean>(false);
  const [giftedCandidate, setGiftedCandidate] =
    useState<CandidateProfileApi | null>(null);

  const { remainingAmount } = useProfileStore();
  const { getLikesCount } = useSummariesStore();
  const { removeCandidate } = useDiscoveryStore();

  const openSendGiftPopup = () => {
    setGiftedCandidate(candidate);
  };

  const closeSendGift = async (swipeRight?: boolean) => {
    setGiftedCandidate(null);

    if (!swipeRight) return; // means gift was not sent, popup was just closed

    setGiftSent(true);

    if (relationKind === RelationKind.KIND_UNSPECIFIED) {
      await removeCandidate(candidate.hid);
    }

    if (relationKind === RelationKind.KIND_LIKES_YOU) {
      await removeCandidate(candidate.hid);
      void getLikesCount();
    }
  };

  const isShowGiftPopup = giftedCandidate !== null;

  if (giftSent || relationKind === RelationKind.KIND_FIRST_MOVE) return null;

  return (
    <>
      <InputContent style={sendGiftWrapper}>
        <div style={sendGiftContainer} onClick={openSendGiftPopup}>
          <div style={sendGiftTextWrapper}>
            <div style={sendGiftTitle}>
              {candidate.gender === Gender.MALE
                ? t('profile.send_gift_banner.title.m')
                : t('profile.send_gift_banner.title.f')}
            </div>
            <div style={sendGiftButton}>
              {t('profile.send_gift_banner.button')}
            </div>
          </div>
          <div style={animationContainer}>
            <div style={animationWrapper}>
              <LottieAnimation
                width={280}
                height={280}
                speed={0.5}
                color="#FFB535"
                animationData={animData}
                shouldAnimate={true}
                loop={true}
                styles={{
                  pointerEvents: 'none'
                }}
              />
            </div>
            <div style={iconContainer}>
              <FireReactionShape
                width={45}
                height={45}
                fillColor="#fff"
                style={{ marginTop: '2px' }}
              />
            </div>
          </div>
        </div>
      </InputContent>
      <Modal fullScreen open={isShowGiftPopup} handleClose={closeSendGift}>
        <SendGiftPopup
          giftedContactId={candidate.hid}
          giftedContactName={candidate.name}
          giftedContactPhoto={candidate.v2photos[0]}
          handleClose={closeSendGift}
          remainingAmount={remainingAmount}
          placement={ProductInvoicePlacements.PROFILE_PAGE_GET_REACTIONS}
          hideBackButton={false}
        />
      </Modal>
    </>
  );
};
